import React, { useState } from "react";
import {
    Box,
    Typography,
    Skeleton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { FiChevronDown } from "react-icons/fi";

// Formatting function for numbers

// Bar chart inside table
const BarChartInTable: React.FC<{
    value: number;
    maxValue: number;
    totalValue: number;
}> = ({ value, maxValue, totalValue }) => {
    const barWidth = (value / maxValue) * 100;
    const percentage = ((value / totalValue) * 100).toFixed(0);

    return (
        <Box sx={{ position: "relative", width: "100%", height: "10px" }}>
            {/* Bar */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: `${barWidth}%`,
                    height: "100%",
                    bgcolor: "#d3d3d3",
                    borderRadius: "5px",
                }}
            />

            {/* Percentage Label */}
            <Typography
                variant="caption"
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: `50%`,
                    transform: "translate(-50%, -50%)",
                    fontSize: "0.65rem",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                }}
            >
                {percentage}%
            </Typography>
        </Box>
    );
};

const capitalizeFirstLetter = (label: string): string => {
    if (!label) return "";
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
};

// Explicit type for data
interface DataItem {
    label: string;
    value: number;
    change: number | null;
}

interface ChartWithTableProps {
    title: string;
    data: DataItem[]; // Explicitly typing the 'data' prop
    defaultExpanded?: boolean;
}

const ClientTable: React.FC<ChartWithTableProps> = ({
    title,
    data,
    defaultExpanded = true,
}) => {
    const values = data.map((d) => {
        const val = typeof d.value === "string" ? Number(d.value) : d.value;
        return isNaN(val) ? 0 : val;
    });

    const maxValue = Math.max(...values);
    const totalValue = values.reduce((sum, val) => sum + val, 0);

    const sortedData = [...data].sort((a, b) => b.value - a.value);

    const rows = sortedData.map((item, index) => ({
        id: index,
        label: capitalizeFirstLetter(item.label),
        bar: item.value,
        value: item.value,
        change: item.change,
    }));

    const columns: GridColDef[] = [
        {
            field: "label",
            headerName: "Label",
            flex: 1.5,
            headerAlign: "center",
            align: "left",
        },
        {
            field: "bar",
            headerName: "Chart",
            flex: 1,
            sortable: false,
            headerAlign: "center",
            align: "left",
            renderCell: (params) => (
                <BarChartInTable
                    value={params.value}
                    maxValue={maxValue}
                    totalValue={totalValue}
                />
            ),
        },
        {
            field: "value",
            headerName: "Value",
            flex: 1,
            align: "right",
            headerAlign: "center",
        },
        // {
        //     field: "change",
        //     headerName: "Δ",
        //     flex: 1,
        //     align: "right",
        //     headerAlign: "center",
        //     cellClassName: (params) => {
        //         if (params.value == null) return "";

        //         // Convert to string and remove last char if it's a letter
        //         let value = params.value.toString();

        //         // Remove first char if it's a €
        //         if (value.startsWith("€")) {
        //             value = value.slice(1);
        //         }

        //         if (/[a-zA-Z]$/.test(value)) {
        //             value = value.slice(0, -1); // remove last char
        //         }

        //         const numericValue = parseFloat(value);
        //         if (isNaN(numericValue)) return "";

        //         return numericValue >= 0
        //             ? "positive-change"
        //             : "negative-change";
        //     },
        // },
    ];

    const minHeight = 50;
    const maxHeight = 120;

    const isLoading = data.length === 0;

    const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? true);

    if (isLoading) {
        return (
            <Box mb={1}>
                <Typography
                    variant="subtitle2"
                    gutterBottom
                    align="left"
                    fontSize="0.875rem"
                    sx={{ fontWeight: "bold" }}
                >
                    {title}
                </Typography>
                <Box>
                    {/* Render 3 Skeleton rows to simulate loading state */}
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            height={15}
                            animation="wave"
                            sx={{ mb: 1, borderRadius: 1 }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            square
            sx={{
                mb: 1,
                boxShadow: "none",
                border: "none",
                "&::before": {
                    display: "none", // removes the default divider line
                },
            }}
        >
            <AccordionSummary
                expandIcon={<FiChevronDown />}
                sx={{
                    p: 0, // removes all padding
                    m: 0, // removes all margin
                    minHeight: 0,
                    "& .MuiAccordionSummary-content": {
                        m: 0,
                        p: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                        p: 0, // optional: tighten up the expand icon padding
                        m: 0,
                    },
                }}
            >
                <Typography
                    variant="subtitle2"
                    fontSize="0.875rem"
                    sx={{ fontWeight: "bold" }}
                >
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ overflow: "hidden" }}>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            maxHeight,
                            minHeight,
                        }}
                    >
                        <DataGridPro
                            rows={rows}
                            columns={columns}
                            rowHeight={30}
                            hideFooter
                            disableColumnMenu
                            disableRowSelectionOnClick
                            density="compact"
                            slots={{
                                columnHeaders: () => null,
                            }}
                            sx={{
                                overflow: "hidden",
                                border: "none",
                                "& .MuiDataGrid-root": {
                                    border: "none",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    borderBottom: "none",
                                },
                                "& .MuiDataGrid-cell": {
                                    fontSize: "0.75rem",
                                    padding: "1px",
                                    borderBottom: "none",
                                    borderTop: "none",
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .positive-change": {
                                    color: "#009A0A",
                                },
                                "& .negative-change": {
                                    color: "#FF365E",
                                },
                                // Hover to show scrollbar
                                "&:hover": {
                                    "&::-webkit-scrollbar": {
                                        opacity: 1, // Make scrollbar visible on hover
                                    },
                                },
                                "&::-webkit-scrollbar": {
                                    width: "8px", // Width of the scrollbar
                                    opacity: 0, // Initially hide the scrollbar
                                    transition: "opacity 0.3s", // Smooth transition
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1", // Light track color
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "#888", // Scrollbar thumb color
                                    // borderRadius: "4px", // Rounded corners for the scrollbar thumb
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#555", // Hover effect for the scrollbar thumb
                                },
                            }}
                        />
                    </div>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ClientTable;
